import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "page-body" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 1,
  class: "page-content"
}
const _hoisted_4 = { class: "company" }
const _hoisted_5 = {
  key: 0,
  class: "brand"
}
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "infos" }
const _hoisted_8 = {
  key: 0,
  class: "name"
}
const _hoisted_9 = {
  key: 1,
  class: "document"
}
const _hoisted_10 = {
  key: 2,
  class: "address"
}
const _hoisted_11 = {
  key: 3,
  class: "address"
}
const _hoisted_12 = {
  key: 2,
  class: "page-content"
}
const _hoisted_13 = { class: "analyse" }
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = {
  key: 3,
  class: "page-content"
}
const _hoisted_16 = { class: "employee" }
const _hoisted_17 = ["innerHTML"]
const _hoisted_18 = {
  key: 4,
  class: "page-content"
}
const _hoisted_19 = { class: "methods" }
const _hoisted_20 = ["innerHTML"]
const _hoisted_21 = {
  key: 5,
  class: "page-content"
}
const _hoisted_22 = { class: "forms" }
const _hoisted_23 = ["innerHTML"]
const _hoisted_24 = {
  key: 6,
  class: "page-content"
}
const _hoisted_25 = { class: "samples" }
const _hoisted_26 = ["innerHTML"]
const _hoisted_27 = {
  key: 7,
  class: "page-content"
}
const _hoisted_28 = { class: "equipments" }
const _hoisted_29 = ["innerHTML"]
const _hoisted_30 = {
  key: 8,
  class: "page-content"
}
const _hoisted_31 = { class: "signatures" }
const _hoisted_32 = { class: "image" }
const _hoisted_33 = ["src"]
const _hoisted_34 = ["src"]
const _hoisted_35 = { class: "name" }
const _hoisted_36 = { class: "type" }
const _hoisted_37 = { class: "date" }
const _hoisted_38 = { class: "document" }

import { computed, ref, onMounted, watch } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";

import {
  getDate,
  getDateAndHour,
  getDifference,
  getDiffBreaks,
} from "@/common/utils/dates";
import { paginateText, getMarkdownBlockHeights } from "@/common/utils/text";

import { fileBase64 } from "@/common/api/rest/file/file";
import { fileAvatarFileShow } from "@/common/api/rest/file/avatar";

import type { AnalysisFullType } from "@/common/types/analysis";

import { pageExport } from "@/components";

interface ContentType {
  title: string | null;
  type: string;
  break: boolean;
  content: any;
}

interface PageType {
  page: number;
  content: {
    [key: string]: ContentType | null;
  } | null;
}

const PHOTO_START = "\x1E";
const PHOTO_FIELD = "\x1F";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    data: {},
    width: {},
    scale: {},
    footer: {},
    filters: {}
  },
  setup(__props: any) {

const props = __props;

const { t, tm } = useI18n();

const width = ref("210mm");
const height = ref(950);
const heightTitle = ref(50);
const minSpaceForContent = ref(100);

const pages = ref<PageType[]>([]);

const summary = computed(
  () => tm("report.types.summary") as Record<string, string>,
);

// Marcadores para bloco atômico de foto
function parseMetadata(raw: any): any {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getAccountId(): string {
  const data: any = props.data;
  return data?.account_id ?? data?.evaluation?.account_id ?? "";
}

function extractImageData(
  payload: any,
): { mimeType: string; base64: string } | null {
  if (!payload) return null;

  if (typeof payload === "string") {
    const s = payload.trim();
    if (!s) return null;
    if (s.startsWith("data:")) {
      const match = s.match(/^data:([^;]+);base64,(.+)$/);
      if (match) return { mimeType: match[1], base64: match[2] };
      return null;
    }
    return { mimeType: "image/png", base64: s };
  }

  const rawMime =
    payload.mime_type ?? payload.mimeType ?? payload.infos ?? "image/png";

  let mimeType = String(rawMime)
    .replace(/^data:/, "")
    .replace(/;base64$/, "");
  const mimeMatch = mimeType.match(/^([^;]+);base64$/);
  if (mimeMatch) mimeType = mimeMatch[1];
  if (!mimeType) mimeType = "image/png";

  const rawBase64 =
    payload.file ?? payload.base64 ?? payload.data ?? payload.content ?? null;

  if (!rawBase64) return null;

  if (typeof rawBase64 === "string" && rawBase64.startsWith("data:")) {
    const m = rawBase64.match(/^data:([^;]+);base64,(.+)$/);
    if (m) return { mimeType: m[1], base64: m[2] };
  }

  return { mimeType, base64: String(rawBase64) };
}

function toDataUrl(payload: any): string | null {
  const parsed = extractImageData(payload);
  if (!parsed) return null;
  return `data:${parsed.mimeType};base64,${parsed.base64}`;
}

/**
 * Busca a imagem e devolve APENAS a tag <img>, sem markdown de título.
 */
async function renderPhotoToHtml(
  accountId: string,
  fileId: string,
): Promise<string | null> {
  try {
    const response = await fileBase64(accountId, fileId);
    const { success, data } = response ?? {};
    if (!success) return null;
    const dataUrl = toDataUrl(data);
    if (!dataUrl) return null;
    return `<img src="${dataUrl}" style="width: auto; height: 200px;" />`;
  } catch (err) {
    console.error("[report] renderPhotoToHtml error:", err);
    return null;
  }
}

/**
 * Insere um bloco atômico de foto no markText.
 * Formato: \x1E title \x1F description \x1F imageHtml \x1E
 */
async function appendPhotoBlock(
  markText: string,
  accountId: string,
  fileId: string,
  title: string,
  description: string | null,
): Promise<string> {
  const imageHtml = await renderPhotoToHtml(accountId, fileId);
  if (!imageHtml) {
    console.warn(`[report] Não consegui renderizar foto ${fileId}`);
    return markText;
  }

  // Remove marcadores residuais sem usar regex (evita no-control-regex)
  const desc = (description ?? "")
    .split(PHOTO_START)
    .join("")
    .split(PHOTO_FIELD)
    .join("");

  const safeTitle = title
    .split(PHOTO_START)
    .join("")
    .split(PHOTO_FIELD)
    .join("");

  return (
    markText +
    `${PHOTO_START}${safeTitle}${PHOTO_FIELD}${desc}${PHOTO_FIELD}${imageHtml}${PHOTO_START}\n`
  );
}

async function fetchEquipmentAvatar(
  accountId: string,
  equipment: any,
): Promise<string | null> {
  if (equipment?.avatar?.file) {
    const directUrl = toDataUrl(equipment.avatar.file);
    if (directUrl) return directUrl;
  }
  if (equipment?.avatar?.base64) return equipment.avatar.base64;
  if (equipment?.avatar?.large && equipment.avatar.large.startsWith("data:")) {
    return equipment.avatar.large;
  }

  const identifier = equipment?.avatar?.avatar_id ?? equipment?.equipment_id;
  if (!identifier) return null;

  try {
    const response = await fileAvatarFileShow(accountId, identifier);

    if (response?.success && response.data) {
      const dataUrl = toDataUrl(response.data);
      if (dataUrl) return dataUrl;
    }
  } catch (err) {
    console.warn(
      `[report] erro ao buscar avatar do equipamento ${equipment?.equipment_id}:`,
      err,
    );
  }

  return null;
}

function resolveOptionLabel(
  questionMeta: any,
  answerValue: any,
  fallback?: string,
): string {
  const options: any[] = questionMeta?.options ?? [];
  if (!options.length) return fallback ?? String(answerValue ?? "");

  const found = options.find(
    (opt: any) => String(opt.value) === String(answerValue),
  );

  return found?.option ?? fallback ?? String(answerValue ?? "");
}

async function paginate() {
  if (!props.data) return;

  pages.value = [];
  let PAGE = 0;
  let HEIGHT_INTERNAL = 0;

  pages.value.push({ page: PAGE, content: {} });

  const paginateTitle = () => {
    let title = "";
    title += `<strong>${t("report.labels.evaluation").toUpperCase()}:</strong>`;
    title += `&nbsp; ${props.data.evaluation.code}`;
    title += "&nbsp; &nbsp; &nbsp; &nbsp;";
    title += `<strong>${t("report.labels.analyse").toUpperCase()}:</strong>`;
    title += `&nbsp; ${props.data.code}`;

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      title: { title, type: "TITLE", break: false, content: "" },
    };

    HEIGHT_INTERNAL += heightTitle.value;
  };

  const newPage = (field: string, type: string, title: string | null) => {
    PAGE++;
    pages.value[PAGE] = { page: PAGE, content: {} };
    HEIGHT_INTERNAL = 0;
    paginateTitle();

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      [field]: { title, type, break: false, content: "" },
    };
  };

  const hasSpaceForContent = (requiredHeight: number): boolean =>
    HEIGHT_INTERNAL + requiredHeight <= height.value;

  const paginateCompany = () => {
    if (!props.data?.company) return;

    const HEITHG_COMPANY_AVATAR = 90;
    const COMPANY_MARGIN = 20;

    const {
      avatar,
      name,
      document_name,
      document_number,
      address: addresses,
      address_id,
      contacts: contactsAll,
      contact_id,
    } = props.data!.company || {};

    const companyNameInfos = paginateText(name?.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    let documentText = "";
    if (document_name) documentText += document_name;
    if (document_number) documentText += ` ${document_number}`;

    const companyDocumentInfos = paginateText(documentText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    const address = addresses?.filter(
      (item) => item.address_id === address_id,
    )?.[0];

    let addressText = "";
    if (address?.address) addressText += address.address;
    if (address?.number) addressText += `, ${address.number}`;
    if (address?.complement) addressText += `, ${address.complement}`;
    if (address?.district) addressText += `, ${address.district}`;
    if (address?.city) addressText += `, ${address.city}`;
    if (address?.state) addressText += `, ${address.state}`;
    if (address?.country) addressText += `, ${address.country}`;
    if (address?.cep) addressText += `, CEP ${address.cep}`;

    const companyAdressInfos = paginateText(addressText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const contacts = contactsAll?.filter(
      (item) => item.contact_id === contact_id,
    )?.[0];

    let contactText = "";
    if (contacts?.name) contactText += contacts.name;
    if (contacts?.contact) contactText += ` - ${contacts.contact}`;

    const companyContactInfos = paginateText(contactText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const companyNameHeight = companyNameInfos
      .map((i) => i.height)
      .reduce((p, n) => p + n, 0);
    const companyDocumentHeight = companyDocumentInfos
      .map((i) => i.height)
      .reduce((p, n) => p + n, 0);
    const companyAdressHeight = companyAdressInfos
      .map((i) => i.height)
      .reduce((p, n) => p + n, 0);
    const companyContactHeight = companyContactInfos
      .map((i) => i.height)
      .reduce((p, n) => p + n, 0);

    const HEITHG_COMPANY_INFOS =
      companyNameHeight +
      companyDocumentHeight +
      companyAdressHeight +
      companyContactHeight +
      12;

    const HEIGHT_TOTAL =
      Math.max(HEITHG_COMPANY_AVATAR, HEITHG_COMPANY_INFOS) + COMPANY_MARGIN;

    if (!hasSpaceForContent(heightTitle.value + HEIGHT_TOTAL)) {
      newPage("company", "COMPANY", null);
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
    }

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      company: {
        title: null,
        type: "COMPANY",
        break: false,
        content: {
          avatar,
          name: companyNameInfos[0]?.text,
          document: companyDocumentInfos[0]?.text,
          address: companyAdressInfos[0]?.text,
          contact: companyContactInfos[0]?.text,
        },
      },
    };

    HEIGHT_INTERNAL += HEIGHT_TOTAL;
  };

  const paginateAnalyse = () => {
    if (!props.data) return;
    const analyse = props.data;

    let markText = "";
    markText += `#### ${t("report.labels.evaluation").toUpperCase()} ${analyse.evaluation.code}`;
    markText += ` &nbsp; | &nbsp; ${t("report.labels.analyse").toUpperCase()} ${analyse.code}\n`;
    markText += `${analyse.description ?? ""}\n\n`;
    markText += `- **${t("report.labels.status")}:** &nbsp; ${t(`enums.analysis_status.${analyse.status}`).toUpperCase()}\n`;

    if (analyse.start_date) {
      const diffTime = getDiffBreaks(
        analyse.start_edited ?? analyse.start_date,
        analyse.end_date
          ? (analyse.end_edited ?? analyse.end_date)
          : new Date().toISOString(),
        analyse.breaks ?? [],
      );
      markText += `- **${t("report.labels.start")}:** &nbsp; ${getDateAndHour(analyse.start_edited ?? analyse.start_date)}`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; ${getDateAndHour(analyse.end_edited ?? analyse.end_date)}`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** &nbsp; ${diffTime.text}\n`;
    } else {
      markText += `- **${t("report.labels.start")}:** &nbsp; --/--/---- --:--:--`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; --/--/---- --:--:--`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** 0m 0s\n`;
    }

    if (analyse.technical) {
      markText += `- **${t("report.labels.technical")}:** &nbsp; ${analyse.technical.name}\n`;
    }

    if (analyse.assistant) {
      if (analyse.technical) markText += "&nbsp; | &nbsp; ";
      else markText += "- ";
      markText += `**${t("report.labels.assistant")}:** &nbsp; ${analyse.assistant.name}\n`;
    } else {
      markText += "\n";
    }

    markText += `- **${t("report.labels.degree_of_risk")}:** &nbsp; ${analyse.danger}\n`;

    if (analyse.status === "CANCELED") {
      markText += `#### ${t("report.labels.cancellation")}\n`;
      markText += `- **${t("report.labels.date")}:** &nbsp; ${getDateAndHour(analyse.canceled_date)}\n`;
      markText += `- **${t("report.labels.motive")}:** &nbsp; ${analyse.canceled_description ?? ""}\n`;
    }

    if (analyse.breaks?.length) {
      markText += "---\n";
      markText += `#### ${t("report.labels.pauses").toUpperCase()}\n\n`;
      analyse.breaks.forEach((item, index) => {
        const pauseDiffTime = getDifference(
          item.start_date,
          item.end_date ?? new Date().toISOString(),
        );
        markText += `&nbsp; &nbsp; **${index + 1} - ${item.motive}**\n\n`;
        if (item.description) markText += `&nbsp; &nbsp; ${item.description}\n`;
        markText += `- **${t("report.labels.start")}:** &nbsp; ${getDateAndHour(item.start_date)}`;
        markText += ` &nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; ${getDateAndHour(item.end_date)}`;
        markText += ` &nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** &nbsp; ${pauseDiffTime.text}\n`;
        if ((analyse.breaks?.length ?? 0) > index + 1) markText += "---\n";
      });
    }

    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("analyse", "ANALYSE", t("report.titles.analyse"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        analyse: {
          title: t("report.titles.analyse"),
          type: "ANALYSE",
          break: false,
          content: "",
        },
      };
    }

    let pageText = "";
    const lines = markText.split("\n");

    for (let i = 0; i < lines.length; i++) {
      const text = lines[i];
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });
      const blockHeight = (pragraph[0]?.height ?? 0) + 3;

      if (!hasSpaceForContent(blockHeight)) {
        if (pageText.trim()) {
          pages.value[PAGE]!.content!.analyse!.content = marked(pageText);
        }
        newPage("analyse", "ANALYSE", null);
        pageText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        pageText += `${text}\n`;
      }
    }

    if (pageText.trim()) {
      pages.value[PAGE]!.content!.analyse!.content = marked(pageText);
    }
  };

  const paginateEmployee = () => {
    if (!props.data?.employee) return;
    const employee = props.data.employee;

    let markText = "";
    if (employee.social_name) markText += `#### ${employee.social_name}\n`;
    else markText += `#### ${employee.name}\n`;
    markText += `${employee.document_name?.toUpperCase()} ${employee.document_number ?? ""}`;
    if (employee.registration_number) {
      markText += ` &nbsp; | &nbsp; ${t("report.labels.registration").toUpperCase()}: &nbsp; ${employee.registration_number ?? ""}`;
    }
    markText += "\n\n";
    markText += `${employee.description ?? ""}\n`;
    markText += `- **${t("report.labels.role")}:** &nbsp; ${employee.role ?? ""}\n`;
    markText += `- **${t("report.labels.sector")}:** &nbsp;  ${employee.sector ?? ""}\n`;

    if (employee.start_date) {
      markText += `- **${t("report.labels.bond")}:** &nbsp; ${getDate(employee.start_date)}`;
      if (employee.end_date) markText += ` - ${getDate(employee.end_date)}\n`;
      else markText += ` - ${t("report.labels.current")} \n`;
    }

    if (employee.ges_name) {
      markText += `- **${t("report.labels.ges_name")}:** &nbsp;  ${employee.ges_name ?? ""}\n`;
    }
    if (employee.ges_status) {
      markText += `- **${t("report.labels.ges_status")}:**  &nbsp;  ${employee.ges_status ? t("common.labels." + employee.ges_status?.toLocaleLowerCase()) : ""}\n`;
    }
    if (employee.ges_description) {
      markText += `- **${t("report.labels.ges_description")}:** &nbsp; ${employee.ges_description ?? ""}\n`;
    }

    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("employee", "EMPLOYEE", t("report.titles.employee"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        employee: {
          title: t("report.titles.employee"),
          type: "EMPLOYEE",
          break: false,
          content: "",
        },
      };
    }

    let pageText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });
      const blockHeight = (pragraph[0]?.height ?? 0) + 3;

      if (!hasSpaceForContent(blockHeight)) {
        if (pageText.trim()) {
          pages.value[PAGE]!.content!.employee!.content = marked(pageText);
        }
        newPage("employee", "EMPLOYEE", null);
        pageText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        pageText += `${text}\n`;
      }
    });

    if (pageText.trim()) {
      pages.value[PAGE]!.content!.employee!.content = marked(pageText);
    }
  };

  const paginateMethod = () => {
    if (!props.data?.methods?.length) return;

    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("methods", "METHODS", t("report.titles.methods"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        methods: {
          title: t("report.titles.methods"),
          type: "METHODS",
          break: false,
          content: "",
        },
      };
    }

    let markText = "";
    props.data.methods?.forEach((item, index) => {
      markText += `#### ${item.code}\n`;
      markText += `<span style="font-size: 0.75em;">${item.name}</span>\n`;

      const metadata = parseMetadata(item.metadata) ?? [];

      metadata?.forEach((field: any) => {
        if (field.type === "RANGE") {
          markText += `- **${field.name}:**`;
          if (field.min) markText += ` &nbsp; ${field.min}`;
          if (field.max) markText += ` a ${field.max}`;
          if (field.measure) markText += ` ${field.measure}`;
          markText += `\n`;
        } else if (
          field.answer !== null &&
          field.answer !== undefined &&
          field.answer !== ""
        ) {
          markText += `- **${field.name}:** &nbsp; ${field.answer}\n`;
        }
      });

      if ((props.data.methods?.length ?? 0) > index + 1) markText += "---\n";
    });

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });
      const blockHeight = (pragraph[0]?.height ?? 0) + 6;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.methods!.content = marked(methodText);
        }
        newPage("methods", "METHODS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.methods!.content = marked(methodText);
    }
  };

  const paginateForm = async () => {
    if (!props.data?.forms?.length && !props.data?.methods?.length) return;

    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("forms", "FORMS", t("report.titles.field_memory"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        forms: {
          title: t("report.titles.field_memory"),
          type: "FORMS",
          break: false,
          content: "",
        },
      };
    }

    let markText = "";

    const forms = props.data.forms ?? [];
    const methods = props.data.methods ?? [];
    const answers = props.data.answers;
    const accountId = getAccountId();

    const dataAny: any = props.data;
    const globalQuestions: any[] =
      dataAny.questions ?? dataAny.form_questions ?? [];

    if (props.filters?.includes("form_question")) {
      for (const [index, form] of forms.entries()) {
        markText += `### ${form.code.toUpperCase()} &nbsp; | &nbsp; ${form.name.toUpperCase()} \n`;
        markText += `<div style="font-size: 0.75em;">${form.description ?? ""}</div>\n`;

        const formMeta = parseMetadata((form as any).metadata) ?? {};
        const orders: string[] = formMeta.orders ?? [];
        const questions: any[] =
          (form as any).questions ?? globalQuestions ?? [];
        const formAnswer: Record<string, any> =
          answers?.metadata?.form[form.form_id] ?? {};

        // Fallback: sem perguntas estruturadas
        if (!questions.length) {
          for (const [questionId, answerValue] of Object.entries(formAnswer)) {
            if (
              answerValue &&
              typeof answerValue === "object" &&
              (answerValue as any).file_id &&
              (answerValue as any).type === "image"
            ) {
              markText = await appendPhotoBlock(
                markText,
                accountId,
                (answerValue as any).file_id,
                (answerValue as any).name || questionId,
                (answerValue as any).description ?? null,
              );
            } else if (Array.isArray(answerValue)) {
              const activeCheckboxes = answerValue.filter(
                (item: any) => item?.value === true,
              );
              if (activeCheckboxes.length) {
                markText += `- **Questão ID: ${questionId}:** &nbsp;\n`;
                activeCheckboxes.forEach((item: any) => {
                  markText += `  - Opção: ${item.option ?? item.id}\n`;
                });
              }
            } else if (
              answerValue !== null &&
              answerValue !== undefined &&
              answerValue !== ""
            ) {
              markText += `- **Questão ID: ${questionId}:** &nbsp; ${answerValue}\n`;
            }
          }

          if (forms.length > index + 1) markText += "---\n";
          continue;
        }

        for (const id of orders) {
          const question = questions.find((q) => q.question_id === id);
          if (!question) continue;

          const questionMeta = parseMetadata(question.metadata) ?? {};

          if (question.type === "TITLE") {
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${question.name?.toUpperCase()}</div>\n`;
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${question.description ?? ""}</div>\n\n`;
          }

          if (question.type === "INPUT" || question.type === "TEXTAREA") {
            if (
              formAnswer[id] === null ||
              formAnswer[id] === undefined ||
              formAnswer[id] === ""
            ) {
              continue;
            }
            markText += `- **${question.name}:** &nbsp; ${formAnswer[id] ?? ""}\n`;
          }

          if (question.type === "CHECKBOX") {
            const checkboxes = formAnswer[id]?.filter(
              (answer: any) => answer.value === true,
            );
            if (checkboxes?.length) {
              markText += `- **${question.name}:** &nbsp;\n`;
              checkboxes?.forEach((answer: any) => {
                markText += `  - ${answer.option}\n`;
              });
            }
          }

          if (question.type === "RADIO" || question.type === "SELECT") {
            if (
              formAnswer[id] === null ||
              formAnswer[id] === undefined ||
              formAnswer[id] === ""
            ) {
              continue;
            }
            markText += `- **${question.name}:** &nbsp;`;
            const label = resolveOptionLabel(questionMeta, formAnswer[id]);
            markText += `${label}\n`;
          }

          if (question.type === "PHOTO") {
            const photoAnswer = formAnswer[id];
            if (photoAnswer?.file_id) {
              // Insere bloco atômico — título + descrição + imagem
              markText = await appendPhotoBlock(
                markText,
                accountId,
                photoAnswer.file_id,
                question.name,
                question.description ?? null,
              );
            }
          }
        }

        if (forms.length > index + 1) markText += "---\n";
      }
    }

    if (props.filters?.includes("method_question")) {
      if (methods.length) markText += "---\n\n";

      methods.forEach((method, index) => {
        markText += `### ${method.code.toUpperCase()} &nbsp; | &nbsp; ${method.name.toUpperCase()} \n`;
        const methodAnswers =
          answers?.metadata?.method?.[method.method_id] ?? {};

        (method as any).questions?.forEach((item: any) => {
          const methodAnswer = methodAnswers?.[item.question_id];
          const itemMeta = parseMetadata(item.metadata) ?? {};

          if (item.type === "TITLE") {
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${item.name?.toUpperCase()}</div>\n`;
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${item.description ?? ""}</div>\n\n`;
          }
          if (item.type === "INPUT" || item.type === "TEXTAREA") {
            markText += `- **${item.name}:**  ${methodAnswer ?? ""}\n`;
          }
          if (item.type === "CHECKBOX") {
            markText += `- **${item.name}:** &nbsp;\n`;
            methodAnswer
              ?.filter((answer: any) => !!answer.value)
              ?.forEach((answer: any) => {
                markText += `  - ${answer.option}\n`;
              });
          }
          if (item.type === "RADIO" || item.type === "SELECT") {
            markText += `- **${item.name}:** &nbsp;`;
            const label = resolveOptionLabel(itemMeta, methodAnswer);
            markText += `${label}\n`;
          }
        });

        if (methods.length > index + 1) markText += "\n---\n\n";
      });
    }

    // Paginação linha a linha, tratando blocos de foto como unidade atômica
    let methodText = "";

    const lines = markText.split("\n");

    for (const text of lines) {
      // -------- BLOCO ATÔMICO DE FOTO --------
      if (
        text.startsWith(PHOTO_START) &&
        text.endsWith(PHOTO_START) &&
        text.length > 2
      ) {
        const inner = text.slice(1, -1);
        const [title, description, imageHtml] = inner.split(PHOTO_FIELD);

        // Altura do título (linha h4)
        const titleMd = `#### ${title.toUpperCase()}`;
        const titleHeight =
          getMarkdownBlockHeights(titleMd, {
            width: "617px",
            font_size: "0.75em",
            line_height: "normal",
          })[0]?.height ?? 20;

        // Altura da descrição (se houver)
        const descHeight = description
          ? (getMarkdownBlockHeights(description, {
              width: "617px",
              font_size: "0.75em",
              line_height: "normal",
            })[0]?.height ?? 0) + 5
          : 0;

        // Altura da imagem + margens
        const imageHeight = 240;

        const totalHeight = titleHeight + descHeight + imageHeight + 20;

        // Se não cabe na página atual, força nova página ANTES do bloco
        if (!hasSpaceForContent(totalHeight)) {
          if (methodText.trim()) {
            pages.value[PAGE]!.content!.forms!.content = marked(methodText);
          }
          newPage("forms", "FORMS", null);
          methodText = "";
        }

        // Adiciona o bloco completo
        methodText += "\n&nbsp;\n";
        methodText += titleMd + "\n";
        if (description) methodText += description + "\n";
        methodText += "\n" + imageHtml + "\n\n";

        HEIGHT_INTERNAL += totalHeight;
        continue;
      }

      // -------- LINHA NORMAL --------
      const hasImage = text.includes("<img") && text.includes("height:");
      let blockHeight = 0;

      if (hasImage) {
        const heightMatch = text.match(/height:\s*(\d+)px/);
        const imageHeight = heightMatch ? parseInt(heightMatch[1]) : 200;
        blockHeight = imageHeight + 40;
      } else {
        const pragraph = getMarkdownBlockHeights(text, {
          width: "617px",
          font_size: "0.75em",
          line_height: "normal",
        });
        blockHeight = (pragraph[0]?.height ?? 0) + 7;
      }

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.forms!.content = marked(methodText);
        }
        newPage("forms", "FORMS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    }

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.forms!.content = marked(methodText);
    }
  };

  const paginateSamples = () => {
    if (!props.data?.samples?.length) return;

    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("samples", "SAMPLES", t("report.titles.samples"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        samples: {
          title: t("report.titles.samples"),
          type: "SAMPLES",
          break: false,
          content: "",
        },
      };
    }

    const samples = props.data.samples;
    let markText = "";

    for (const [index, sample] of samples.entries()) {
      markText += `- **${t("report.labels.name")}:** &nbsp; ${sample.name}\n`;
      markText += `- **${t("report.labels.code")}:** &nbsp; ${sample.code}\n`;
      if (sample.method) {
        markText += `- **${t("report.labels.method")}:** &nbsp; ${sample.method.code} &nbsp; | &nbsp; ${sample.method.name}\n`;
      } else markText += "\n";
      markText += `- **${t("report.labels.validity_date")}:** &nbsp; ${getDate(sample.validity)}\n`;
      markText += `- **${t("report.labels.description")}:** &nbsp; ${sample.description ?? ""}\n`;
      if (samples.length > index + 1) markText += "---\n";
    }

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });
      const blockHeight = (pragraph[0]?.height ?? 0) + 5;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.samples!.content = marked(methodText);
        }
        newPage("samples", "SAMPLES", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.samples!.content = marked(methodText);
    }
  };

  const paginateEquipments = async () => {
    if (!props.data?.equipments?.length) return;

    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("equipments", "EQUIPMENTS", t("report.titles.equipments"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        equipments: {
          title: t("report.titles.equipments"),
          type: "EQUIPMENTS",
          break: false,
          content: "",
        },
      };
    }

    const equipments = props.data.equipments;
    const accountId = getAccountId();
    let markText = "";

    for (const [index, item] of equipments.entries()) {
      const avatarSrc = await fetchEquipmentAvatar(accountId, item);

      markText += `<div style="display: flex; flex-direction: row; gap: 2em; align-items: flex-start; margin-top: 0.5em;">`;
      markText += `<div style="width: 100px; height: auto; background: #f5f5f5;">`;
      if (avatarSrc) {
        markText += `<img src="${avatarSrc}" style="width: 100%; height: auto;" />`;
      }
      markText += `</div>`;
      markText += `<div style="display: flex; flex-direction: row; gap: 2em;">`;
      markText += `<ul style="list-style-type: none;">`;
      markText += `<li><strong>${t("report.labels.code")}:</strong> ${item.code}</li>`;
      markText += `<li><strong>${t("report.labels.name")}:</strong> ${item.name}</li>`;
      markText += `<li><strong>${t("report.labels.model")}:</strong> ${item.model}</li>`;
      markText += `<li><strong>${t("report.labels.brand")}:</strong> ${item.brand}</li>`;
      markText += `</ul>`;
      markText += `<ul style="list-style-type: none;">`;
      markText += `<li><strong>${t("report.labels.serial_number")}:</strong> ${item.serial_number}</li>`;
      markText += `<li><strong>${t("report.labels.fabrication_date")}:</strong> ${getDate(item.fabrication_date)}</li>`;
      markText += `<li><strong>${t("report.labels.validity_date")}:</strong> ${getDate(item.expired_date)}</li>`;
      markText += `</ul>`;
      markText += `</div>`;
      markText += `</div>\n`;

      if (equipments.length > index + 1) markText += "<hr>\n";
    }

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });
      const blockHeight = (pragraph[0]?.height ?? 0) + 6;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.equipments!.content = marked(methodText);
        }
        newPage("equipments", "EQUIPMENTS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.equipments!.content = marked(methodText);
    }
  };

  const paginateSigns = () => {
    if (!props.data?.signatures?.length) return;

    const SIGN_SIZE = 140;
    const SIGN_GAP = 25;

    if (!hasSpaceForContent(heightTitle.value + SIGN_SIZE)) {
      newPage("signatures", "SIGNATURES", t("report.titles.signatures"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        signatures: {
          title: t("report.titles.signatures"),
          type: "SIGNATURES",
          break: false,
          content: [],
        },
      };
    }

    const signs = props.data?.signatures ?? [];
    let SIGNATURES: any[] = [];
    let ITEMS = 0;

    signs.forEach((sign, index) => {
      ITEMS++;

      if (ITEMS === 1) {
        if (!hasSpaceForContent(SIGN_SIZE)) {
          if (SIGNATURES.length > 0) {
            pages.value[PAGE]!.content!.signatures!.content = [...SIGNATURES];
          }
          newPage("signatures", "SIGNATURES", null);
          SIGNATURES = [sign];
          HEIGHT_INTERNAL += SIGN_SIZE;
        } else {
          SIGNATURES.push(sign);
          HEIGHT_INTERNAL += SIGN_SIZE;
        }
      } else if (ITEMS === 2) {
        SIGNATURES.push(sign);
        if (signs.length > index + 1) {
          HEIGHT_INTERNAL += SIGN_GAP;
          ITEMS = 0;
        }
      }
    });

    if (SIGNATURES.length > 0) {
      pages.value[PAGE].content!.signatures!.content = [...SIGNATURES];
    }
  };

  paginateTitle();

  if (props.filters?.includes("company")) paginateCompany();
  if (props.filters?.includes("analyse")) paginateAnalyse();
  if (props.filters?.includes("employee")) paginateEmployee();
  if (props.filters?.includes("method")) paginateMethod();

  if (
    props.filters?.includes("form_question") ||
    props.filters?.includes("method_question")
  ) {
    await paginateForm();
  }

  if (props.filters?.includes("sample")) paginateSamples();

  if (props.filters?.includes("equipment")) {
    await paginateEquipments();
  }

  if (props.filters?.includes("signature")) paginateSigns();
}

watch(
  () => props.data,
  () => paginate(),
);

onMounted(() => {
  paginate();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (page) => {
    return (_openBlock(), _createBlock(_unref(pageExport), {
      key: page.page,
      width: width.value,
      page: page.page + 1,
      scale: __props.scale,
      "show-footer": __props.footer?.visible,
      "footer-title": __props.footer?.title,
      "footer-subtitle": __props.footer?.subtitle
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.values(page.content!), (item, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
              (item!.title)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "page-title",
                    innerHTML: item!.title
                  }, null, 8, _hoisted_2))
                : _createCommentVNode("", true),
              (item!.type === 'COMPANY')
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      (item!.content.avatar)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createElementVNode("img", {
                              src: item!.content.avatar.base64
                            }, null, 8, _hoisted_6)
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_7, [
                        (item!.content.name)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(item!.content.name), 1))
                          : _createCommentVNode("", true),
                        (item!.content.document)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(item!.content.document), 1))
                          : _createCommentVNode("", true),
                        (item!.content.address)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(item!.content.address), 1))
                          : _createCommentVNode("", true),
                        (item!.content.contact)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(item!.content.contact), 1))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'ANALYSE')
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_14)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'EMPLOYEE')
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_17)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'METHODS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    _createElementVNode("div", _hoisted_19, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_20)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'FORMS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_23)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'SAMPLES')
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                    _createElementVNode("div", _hoisted_25, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_26)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'EQUIPMENTS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                    _createElementVNode("div", _hoisted_28, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_29)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'SIGNATURES')
                ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                    _createElementVNode("div", _hoisted_31, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item!.content, (sign) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: sign.signature_id,
                          class: "signature"
                        }, [
                          _createElementVNode("div", _hoisted_32, [
                            (sign.signature)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: sign.signature,
                                  alt: "Signature"
                                }, null, 8, _hoisted_33))
                              : (_openBlock(), _createElementBlock("img", {
                                  key: 1,
                                  src: require('@/assets/images/signature.png'),
                                  style: {"opacity":"0.25"}
                                }, null, 8, _hoisted_34))
                          ]),
                          _createElementVNode("div", _hoisted_35, _toDisplayString(sign.name), 1),
                          _createElementVNode("div", _hoisted_36, [
                            (sign.type === 'OTHER')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(_toDisplayString(sign.type_name), 1)
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(_toDisplayString(_unref(t)(`common.labels.${sign.type.toLowerCase()}`)), 1)
                                ], 64))
                          ]),
                          _createElementVNode("div", _hoisted_37, _toDisplayString(sign.date), 1),
                          _createElementVNode("div", _hoisted_38, _toDisplayString(sign.document_name) + " " + _toDisplayString(sign.document_number), 1)
                        ]))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ])
      ]),
      _: 2
    }, 1032, ["width", "page", "scale", "show-footer", "footer-title", "footer-subtitle"]))
  }), 128))
}
}

})